import * as React from 'react';
import { connect } from 'react-redux';
import { Route } from 'react-router';
import { HashRouter } from 'react-router-dom';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { isSpaceInfoItemType } from '../../../ts/models/ContentType';
import { UrlTelemetryService } from '../../../ts/services/UrlTelemetryService';
import { NavLocationSelectors } from '../../selectors/NavigationStateSelectors';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo, SpaceType } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { BasePathsUtils } from './BasePathsUtils';

function getAllPaths(urls: SpacesSiteUrls) {
  const sitePaths = BasePathsUtils.getWatchedBasePathsForSite(urls);
  const allPaths = sitePaths.concat(BasePathsUtils.getWatchedBasePathsForServer());
  return allPaths;
}

const memoizedGetAllPaths = defaultMemoize(getAllPaths);

function isReactRouterPath(path: string, urls: SpacesSiteUrls): boolean {
  return _.some(memoizedGetAllPaths(urls), (baseUrl) => {
    return path.startsWith(baseUrl);
  });
}

function stateNameForRR(path: string, spaceInfo: SpaceInfo, inServerArea: boolean) {
  const stateName = (inServerArea && path.startsWith('/server/') ? 'server.' : '') + spaceInfo.spaceType + (_.isUndefined(spaceInfo.subspaceName) ? '' : '.' + spaceInfo.subspaceName);
  // if the stateName is undefined, it returns "undefined" as a string with the empty string concatenation,
  // have the function return undefined instead of the string.
  if (stateName === 'undefined') {
    return undefined;
  }
  return stateName;
}

export interface TelemetryRouterOwnProps {
  readonly locationPath: string;
  readonly telemetryService: UrlTelemetryService;
}

export interface TelemetryRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly spaceInfo: SpaceInfo;
  readonly inServerArea: boolean;
}

export type TelemetryRouterProps = TelemetryRouterOwnProps & TelemetryRouterStateProps;

interface TelemetryRouterState {
  readonly previousStateName: string;
  readonly previousSpaceType: SpaceType;
}

export class TelemetryRouter extends React.PureComponent<TelemetryRouterProps, TelemetryRouterState> {
  public render(): JSX.Element {
    return null;
  }

  public constructor(props: TelemetryRouterProps) {
    super(props);
    this.state = { previousStateName: undefined, previousSpaceType: undefined };
  }

  public componentDidMount() {
    this.sendVizportalNavigationEvent();
  }

  public componentDidUpdate(prevProps: TelemetryRouterProps) {
    // In a react router world we can remove all the angular stuff from this file,
    // along with the router stuff, all we will need is the space info changes from app store
    if (prevProps.spaceInfo !== this.props.spaceInfo || this.props.locationPath !== prevProps.locationPath) {
      this.sendVizportalNavigationEvent();
    }
  }

  private sendVizportalNavigationEvent() {
    const currentReactRouter = this.props.urls && isReactRouterPath(this.props.locationPath, this.props.urls);
    if (currentReactRouter && _.isUndefined(this.props.spaceInfo.spaceType)) {
      // special case if we're on react router page but invalid spaceinfo
      // means space info hasn't been set yet. Just return and wait.
      return;
    }

    const currentStateName = stateNameForRR(this.props.locationPath, this.props.spaceInfo, this.props.inServerArea);

    // We want to send the space item id as part of navigation event.
    // However the first update will be fired on url change before we've loaded the content item.
    // Another updated is fired when space info changes to include the actual item.
    // So wait (dont fire event and update current state) until that second update. Metrics and Views 
    // have item initialized with id null, so return if item.id null as well.
    // Add a check that only returns on when the space type has changed so that we don't
    // lose sub space changes.
    if (this.state.previousSpaceType !== this.props.spaceInfo.spaceType && isSpaceInfoItemType(this.props.spaceInfo.spaceType) && (this.props.spaceInfo.item === null || this.props.spaceInfo.item?.id === null)) {
      return;
    }

    if (currentStateName && (this.state.previousStateName !== currentStateName
          // for metrics and views, previousStateName = currentStateName when item is populated
          || this.props.spaceInfo.item?.contentType === 'metric' || this.props.spaceInfo.item?.contentType === 'view')) {
      this.props.telemetryService.sendUrlEventsOnStateChange({
        newStateName: currentStateName,
        oldStateName: this.state.previousStateName,
        contentId: this.props.spaceInfo.item != null && this.props.spaceInfo.item.id != null
          ? this.props.spaceInfo.item.id
          : undefined
      });
      this.setState({previousStateName: currentStateName, previousSpaceType: this.props.spaceInfo.spaceType});
    }
  }
}

const mapStateToProps: MapStateToProps<TelemetryRouterStateProps> = state => ({
  spaceInfo: selectSpaceInfo(state),
  urls: siteUrlsSelector(state),
  inServerArea: NavLocationSelectors.inServerArea(state),
});

export const ConnectedTelemetryRouter
  = connect(mapStateToProps)(TelemetryRouter);

export const NavigationTelemetry = (props: TelemetryRouterOwnProps) => {
  const telemetryService = ServiceLocator.get<UrlTelemetryService>('UrlTelemetryService');

  // TFSID:997680. Don't render the HashRouter until we've signed in.
  const server = getSessionManagementServiceInstance();
  if (!server.inSession()) {
    return null;
  }

  return (
    <HashRouter>
      <Route render={(routeProps) =>

        <ConnectedTelemetryRouter
          locationPath={routeProps.location.pathname}
          telemetryService={telemetryService}
        />
      }/>
    </HashRouter>
  );
};
